import React, { useEffect, useRef, useState } from "react";
import "../styles/VisibleFeatures.css";

interface Feature {
  id: number;
  name: string;
  sitetype: string;
  address?: string;
  distanceKm?: number;
  geometry?: { x: number; y: number; spatialReference?: any } | undefined;
}

interface VisibleFeaturesProps {
  features: Feature[];
  searchQuery?: string;
  onFeatureClick?: (feature: Feature) => void;
  onViewOnMap?: (feature: Feature) => void;
  selectedFeature?: any | null;
  isZoomedIn?: boolean;
}

const VisibleFeatures: React.FC<VisibleFeaturesProps> = ({
  features,
  searchQuery,
  onFeatureClick,
  onViewOnMap,
  selectedFeature,
  isZoomedIn = false,
}) => {
  const [liveMessage, setLiveMessage] = useState<string>("");
  const prevCountRef = useRef<number | null>(null);
  const prevSelectedIdRef = useRef<any>(null);
  const hiddenButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const getSitypeIcon = (sitetype: string) => {
    switch (sitetype) {
      case "BusStations":
        return "/icons/swift.svg";
      case "MetroStations":
        return "/icons/metro.svg";
      case "PayzoneAgents":
        return "/icons/payzone.svg";
      default:
        return "/icons/payzone.svg";
    }
  };

  const getSitypeLabel = (sitetype: string) => {
    switch (sitetype) {
      case "BusStations":
        return "Bus Station";
      case "MetroStations":
        return "Metro Station";
      case "PayzoneAgents":
        return "Payzone Agent";
      default:
        return sitetype;
    }
  };

  const getTopUpServiceLabel = (sitetype: string) => {
    switch (sitetype) {
      case "BusStations":
        return "Swift ticket machine";
      case "MetroStations":
        return "Metro ticket machine";
      case "PayzoneAgents":
        return "Payzone Agent";
      default:
        return sitetype;
    }
  };

  const formatDistance = (distanceKm?: number) => {
    if (typeof distanceKm !== "number") return "";
    if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} metres`;
    return `${distanceKm.toFixed(1)} km`;
  };

  const getSitypeIconClass = (sitetype: string) => {
    switch (sitetype) {
      case "BusStations":
        return "wmnds-nearest-stop-station__icon--bus";
      case "MetroStations":
        return "wmnds-nearest-stop-station__icon--metro";
      default:
        return "wmnds-nearest-stop-station__icon--rail";
    }
  };

  const renderPayzoneWarning = () => (
    <div className="wmnds-warning-text wmnds-m-t-sm">
      <svg className="wmnds-warning-text__icon">
        <use
          xlinkHref="#wmnds-general-warning-triangle"
          href="#wmnds-general-warning-triangle"
        ></use>
      </svg>
       We recommend contacting the Payzone agent directly to confirm availability before visiting. Swift top-up services are provided at Payzone agents as a convenience, but are not guaranteed.
    </div>
  );


  // Announce visible features count when it changes
  useEffect(() => {
    const prev = prevCountRef.current;
    const curr = features.length;
    if (prev === null) {
      // initialize but don't announce on first mount
      prevCountRef.current = curr;
    } else if (prev !== curr) {
      const msg =
        curr === 0
          ? "No locations visible"
          : `${curr} location${curr === 1 ? "" : "s"} visible`;
      setLiveMessage(msg);
      prevCountRef.current = curr;
    }
  }, [features]);

  // Announce selected feature details when selection changes
  useEffect(() => {
    const prevId = prevSelectedIdRef.current;
    const currId = selectedFeature
      ? (selectedFeature.id ??
        selectedFeature.ID ??
        selectedFeature.objectid ??
        null)
      : null;
    if (prevId !== currId) {
      if (selectedFeature && currId !== null) {
        const name =
          selectedFeature.name || selectedFeature.Name || "Selected location";
        const st =
          selectedFeature.sitetype ||
          selectedFeature.Type ||
          selectedFeature.type ||
          "";
        const stLabel =
          st === "BusStations"
            ? "Bus station"
            : st === "MetroStations"
              ? "Metro station"
              : st === "PayzoneAgents"
                ? "Payzone agent"
                : st || "location";
        const addr = selectedFeature.address
          ? ` at ${selectedFeature.address}`
          : "";
        setLiveMessage(`${name} selected, ${stLabel}${addr}`);
      }
      prevSelectedIdRef.current = currId;
    }
  }, [selectedFeature]);

  return (
    <div className="visible-features">
      {/* Live region for screen readers: announces visible count and selected feature details */}
      <div
        id="visible-features-live"
        aria-live="polite"
        aria-atomic="true"
        className="wmnds-screenreaders-only"
      >
        {liveMessage}
      </div>
      {selectedFeature ? (
        <div className="selected-feature-details wmnds-m-b-md">
          <div className="wmnds-col-1 wmnds-m-b-lg">
            <div className="wmnds-grid wmnds-grid--align-center">
              <span className="wmnds-col-auto wmnds-m-r-md">
                <img
                  src={getSitypeIcon(
                    selectedFeature.sitetype ||
                      selectedFeature.Type ||
                      selectedFeature.type ||
                      selectedFeature.Sitetype ||
                      "",
                  )}
                  alt="marker"
                  style={{ width: 36, height: 36 }}
                />
              </span>
              <div className="wmnds-col-3-4">
                <h3>
                  {" "}
                  {selectedFeature.name ||
                    selectedFeature.Name ||
                    "Selected location"}
                </h3>
              </div>
            </div>
          </div>

          <>
            <strong>Top-up service</strong>
            <p className="selected-feature-address">
              {getTopUpServiceLabel(
                selectedFeature.sitetype ||
                  selectedFeature.Type ||
                  selectedFeature.type ||
                  selectedFeature.Sitetype ||
                  "",
              )}
            </p>
          </>

          {searchQuery && typeof selectedFeature.distanceKm === "number" && (
            <p className="selected-feature-address">
              <strong>{formatDistance(selectedFeature.distanceKm)}</strong> from
              (<strong>{searchQuery}</strong>)
            </p>
          )}

          {selectedFeature.address && (
            <>
              <strong>Address</strong>
              <p className="selected-feature-address">
                {selectedFeature.address}
              </p>
            </>
          )}

          {/* Always show payzone warning in the details view for PayzoneAgents */}
          {(selectedFeature.sitetype ||
            selectedFeature.Type ||
            selectedFeature.type ||
            selectedFeature.Sitetype) === "PayzoneAgents" &&
            renderPayzoneWarning()}

          {/* Render any additional attributes available */}
          {/* <div className="selected-feature-attrs">
            {Object.entries(selectedFeature as Record<string, any>).map(
              ([key, val]) => {
                const formatValue = (v: any) => {
                  if (v === null || typeof v === "undefined") return "";
                  if (typeof v === "object") {
                    try {
                      const s = JSON.stringify(v, null, 2);
                      return s.length > 300 ? s.slice(0, 300) + "…" : s;
                    } catch (e) {
                      return String(v);
                    }
                  }
                  return String(v);
                };

                return (
                  <div key={key} className="selected-attr">
                    <strong>{key}:</strong>{" "}
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      {formatValue(val)}
                    </span>
                  </div>
                );
              },
            )}
          </div> */}
        </div>
      ) : null}
      {!selectedFeature && (
        <div className="wmnds-nearest-stop-station">
          <div className="wmnds-content-card">
            <div className="wmnds-p-b-md">
              <h2>Nearest top-up locations</h2>
              {/* If the map isn't zoomed in far enough, prompt to zoom in instead of showing the list */}
              {!isZoomedIn ? (
                <div className="wmnds-msg-summary wmnds-msg-summary--info">
                  <div className="wmnds-msg-summary__header">
                    <svg className="wmnds-msg-summary__icon">
                      <use href="#wmnds-general-info"></use>
                    </svg>
                    <h3 className="wmnds-msg-summary__title">
                      Enter location above or Zoom in to view locations
                    </h3>
                  </div>
                </div>
              ) : features.length === 0 ? (
                <div className="no-features">No locations visible</div>
              ) : (
                <>
                  {features.map((feature) => {
                    const fid = String(feature.id);
                    const handleSelect = () => {
                      // Notify parent of selection and explicitly request map zoom
                                      // selection handler invoked
                      onFeatureClick?.(feature);
                      try {
                        onViewOnMap?.(feature);
                      } catch (e) {}
                      const name = feature.name || "Selected location";
                      const st = feature.sitetype || "";
                      const stLabel =
                        st === "BusStations"
                          ? "Bus station"
                          : st === "MetroStations"
                            ? "Metro station"
                            : st === "PayzoneAgents"
                              ? "Payzone agent"
                              : st || "location";
                      const addr = feature.address
                        ? ` at ${feature.address}`
                        : "";
                      setLiveMessage(`${name} selected, ${stLabel}${addr}`);
                      const el = hiddenButtonRefs.current[fid];
                      if (el && typeof el.focus === "function") {
                        setTimeout(() => el.focus(), 0);
                      }
                    };

                    return (
                      <div
                        key={feature.id}
                        className="wmnds-grid wmnds-grid--spacing-2-md wmnds-nearest-stop-station__stop"
                        onClick={() => handleSelect()}
                        onPointerUp={() => handleSelect()}
                        onTouchEnd={() => handleSelect()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSelect();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="wmnds-col-auto">
                          <img
                            src={getSitypeIcon(feature.sitetype)}
                            alt={getSitypeLabel(feature.sitetype)}
                            className={`wmnds-nearest-stop-station__icon ${getSitypeIconClass(feature.sitetype)}`}
                            style={{ width: 30, height: 30 }}
                          />
                        </div>
                        <div className="wmnds-col-auto">
                          <div>
                            <a
                              href="#/"
                              className="nearby-location-link"
                              onClick={(e) => {
                                e.preventDefault();
                                handleSelect();
                              }}
                              onPointerUp={(e) => {
                                e.preventDefault();
                                handleSelect();
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                handleSelect();
                              }}
                            >
                              {feature.name}
                            </a>
                          </div>
                          <span>
                            {feature.address ||
                              getSitypeLabel(feature.sitetype)}
                          </span>

                          {/* Payzone warning shown in details view only */}

                          {searchQuery &&
                            typeof feature.distanceKm === "number" && (
                              <div>
                                <div className="selected-feature-address">
                                  <strong>{formatDistance(feature.distanceKm)}</strong> from (
                                  <strong>{searchQuery}</strong>)
                                </div>
                              </div>
                            )}

                          <button
                            ref={(el) => {
                              hiddenButtonRefs.current[fid] = el;
                            }}
                            id={`feature-focus-${fid}`}
                            className="wmnds-screenreaders-only"
                            onFocus={() => {
                              const name = feature.name || "Selected location";
                              const st = feature.sitetype || "";
                              const stLabel =
                                st === "BusStations"
                                  ? "Bus station"
                                  : st === "MetroStations"
                                    ? "Metro station"
                                    : st === "PayzoneAgents"
                                      ? "Payzone agent"
                                      : st || "location";
                              const addr = feature.address
                                ? ` at ${feature.address}`
                                : "";
                              setLiveMessage(
                                `${name} selected, ${stLabel}${addr}`,
                              );
                            }}
                          >
                            {`Details for ${feature.name}`}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(VisibleFeatures);
